/** Map preview/API errors to recipient ticket page UI states. */
import { TicketStatus, effectiveTicketStatus } from './ticketStatus';

export const TICKET_PAGE_STATE = {
  LOADING: 'loading',
  PREVIEW: 'preview',
  SIGN_IN: 'sign_in',
  COMPLETE_PROFILE: 'complete_profile',
  ACCEPT_READY: 'accept_ready',
  ACCEPTING: 'accepting',
  ACCEPTED: 'accepted',
  ALREADY_ACCEPTED: 'already_accepted',
  PASSED: 'passed',
  PASSED_SHAREABLE: 'passed_shareable',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  NOT_FOUND: 'not_found',
  COULD_NOT_LOAD: 'could_not_load',
};

/**
 * Canonical ticket state-machine names (for logging / reasoning). The UI keys
 * above are a superset; this maps each to the documented lifecycle vocabulary.
 */
export const CANONICAL_TICKET_STATE = {
  [TICKET_PAGE_STATE.LOADING]: 'LOADING_CODE',
  [TICKET_PAGE_STATE.PREVIEW]: 'PREVIEW_READY',
  [TICKET_PAGE_STATE.SIGN_IN]: 'AUTH_REQUIRED',
  [TICKET_PAGE_STATE.COMPLETE_PROFILE]: 'AUTH_REQUIRED',
  [TICKET_PAGE_STATE.ACCEPT_READY]: 'PREVIEW_READY',
  [TICKET_PAGE_STATE.ACCEPTING]: 'ACCEPTING',
  [TICKET_PAGE_STATE.ACCEPTED]: 'ACCEPTED',
  [TICKET_PAGE_STATE.ALREADY_ACCEPTED]: 'ALREADY_ACCEPTED',
  [TICKET_PAGE_STATE.PASSED]: 'DECLINED',
  [TICKET_PAGE_STATE.PASSED_SHAREABLE]: 'PASSED',
  [TICKET_PAGE_STATE.EXPIRED]: 'EXPIRED',
  [TICKET_PAGE_STATE.CANCELED]: 'CANCELED',
  [TICKET_PAGE_STATE.NOT_FOUND]: 'NOT_FOUND',
  [TICKET_PAGE_STATE.COULD_NOT_LOAD]: 'ERROR',
};

export function canonicalTicketState(state) {
  return CANONICAL_TICKET_STATE[state] || 'ERROR';
}

const RISKY_TERMS = /\b(bet|wager|gambling|odds|winnings|escrow|sportsbook)\b/i;

export function assertTicketPageCopy(text) {
  return !RISKY_TERMS.test(String(text || ''));
}

export function mapPreviewErrorToState(message = '') {
  const msg = String(message).toLowerCase();
  if (msg.includes('not found')) return TICKET_PAGE_STATE.NOT_FOUND;
  if (msg.includes('expired')) return TICKET_PAGE_STATE.EXPIRED;
  if (msg.includes('canceled') || msg.includes('cancelled')) return TICKET_PAGE_STATE.CANCELED;
  if (msg.includes('already accepted')) return TICKET_PAGE_STATE.ALREADY_ACCEPTED;
  if (msg.includes('passed on')) return TICKET_PAGE_STATE.PASSED;
  if (msg.includes('not configured') || msg.includes('preview failed')) {
    return TICKET_PAGE_STATE.COULD_NOT_LOAD;
  }
  return TICKET_PAGE_STATE.COULD_NOT_LOAD;
}

export function mapPreviewToState(preview) {
  if (!preview) return TICKET_PAGE_STATE.COULD_NOT_LOAD;
  // Effective status, not raw `preview.status` (audit F7): a just-expired ticket
  // is persisted `OPEN` until the scheduler runs, but `expiresAt` is in the past.
  const status = effectiveTicketStatus(preview);
  if (status === TicketStatus.ACCEPTED) return TICKET_PAGE_STATE.ALREADY_ACCEPTED;
  if (status === TicketStatus.EXPIRED) return TICKET_PAGE_STATE.EXPIRED;
  if (status === TicketStatus.CANCELED) return TICKET_PAGE_STATE.CANCELED;
  if (status === TicketStatus.DECLINED) return TICKET_PAGE_STATE.PASSED;
  if (!preview.openable && preview.reason) {
    return mapPreviewErrorToState(preview.reason);
  }
  return TICKET_PAGE_STATE.PREVIEW;
}

export function resolveTicketPageState({
  loading,
  error,
  preview,
  user,
  profileReady,
  accepting,
  passing = false,
  passComplete = null,
  authReady = true,
  profileLoading = false,
}) {
  if (loading) return TICKET_PAGE_STATE.LOADING;
  if (passComplete) {
    return passComplete.terminal
      ? TICKET_PAGE_STATE.PASSED
      : TICKET_PAGE_STATE.PASSED_SHAREABLE;
  }
  if (error && !preview) return mapPreviewErrorToState(error);
  const base = mapPreviewToState(preview);
  if (base !== TICKET_PAGE_STATE.PREVIEW) return base;
  if (!preview?.openable) {
    return mapPreviewErrorToState(preview?.reason || 'Could not load challenge ticket');
  }
  // Preview resolves independently of auth. Only gate the accept CTA on identity:
  // wait for Firebase auth to settle before deciding sign-in vs. accept so a
  // returning user is never shown the sign-in / complete-profile CTA by mistake.
  if (!authReady) return TICKET_PAGE_STATE.LOADING;
  if (!user?.uid) return TICKET_PAGE_STATE.SIGN_IN;
  if (profileLoading) return TICKET_PAGE_STATE.LOADING;
  if (!profileReady) return TICKET_PAGE_STATE.COMPLETE_PROFILE;
  if (accepting || passing) return TICKET_PAGE_STATE.ACCEPTING;
  return TICKET_PAGE_STATE.ACCEPT_READY;
}
